import React, { useState, useRef, useEffect } from "react";
import { Plus, Trash2, X, Save, ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
function RuleCard({ rule, index, moveRule, onEdit, onDelete, onToggleActive }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: "LINE_RULE",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveRule(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });
  const [{ isDragging }, drag] = useDrag({
    type: "LINE_RULE",
    item: () => {
      return { id: rule.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });
  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));
  return <div
    ref={ref}
    style={{ opacity }}
    data-handler-id={handlerId}
    className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900 hover:shadow-sm transition-shadow mb-2"
  >
      <div className="p-3">
        <div className="flex items-start gap-3">
          {
    /* Drag Handle */
  }
          <div className="cursor-move text-gray-400 dark:text-gray-600 mt-1">
            <GripVertical className="w-4 h-4" />
          </div>

          {
    /* Priority Badge */
  }
          <div className="flex-shrink-0 mt-1">
            <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
              {rule.priority}
            </div>
          </div>

          {
    /* Rule Content */
  }
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">{rule.name}</h5>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
    type="checkbox"
    checked={rule.active}
    onChange={(e) => {
      e.stopPropagation();
      onToggleActive(rule);
    }}
    className="sr-only peer"
  />
                    <div className="w-7 h-4 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-1 peer-focus:ring-blue-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-600" />
                  </label>
                </div>
                
                {!isExpanded && <p className="text-xs text-gray-600 dark:text-gray-400">
                    Type: {rule.actionType}
                    {rule.conditions.length > 0 && ` \u2022 ${rule.conditions.length} condition${rule.conditions.length > 1 ? "s" : ""}`}
                  </p>}
              </div>

              {
    /* Action Buttons */
  }
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
    onClick={(e) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    }}
    className="app-btn-gloss p-1.5"
  >
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />}
                </button>
                <button
    onClick={(e) => {
      e.stopPropagation();
      onEdit(rule);
    }}
    className="app-btn-gloss px-2 py-1 text-xs"
  >
                  Edit
                </button>
                <button
    onClick={(e) => {
      e.stopPropagation();
      onDelete(rule.id);
    }}
    className="app-btn-gloss p-1.5"
  >
                  <Trash2 className="w-3 h-3 text-red-600 dark:text-red-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {
    /* Expanded View */
  }
        {isExpanded && <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 ml-12">
            <div className="space-y-2 text-xs">
              {rule.conditions.length > 0 && <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Conditions ({rule.conditionLogic}):</span>
                  <div className="mt-1 space-y-1 pl-2">
                    {rule.conditions.map((condition, idx) => <div key={condition.id} className="text-gray-600 dark:text-gray-400">
                        {idx > 0 && <span className="font-medium text-blue-600 dark:text-blue-400 mr-1">{rule.conditionLogic}</span>}
                        {condition.field.replace(/_/g, " ")} {condition.operator} "{condition.value}"
                      </div>)}
                  </div>
                </div>}
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Action:</span>{" "}
                <span className="text-gray-600 dark:text-gray-400">{rule.actionType}</span>
                {Object.keys(rule.actionConfig).length > 0 && <div className="mt-1 pl-2 text-gray-600 dark:text-gray-400">
                    {Object.entries(rule.actionConfig).map(([key, value]) => <div key={key}>
                        {key.replace(/([A-Z])/g, " $1").trim()}: {value}
                      </div>)}
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}
function InvoiceConfiguration() {
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedJacket, setSelectedJacket] = useState("");
  const [clients, setClients] = useState([]);
  const [jackets, setJackets] = useState([]);
  const [invoiceElements, setInvoiceElements] = useState([]);
  const [expandedElement, setExpandedElement] = useState(null);
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [invoiceLineRules, setInvoiceLineRules] = useState(() => {
    const saved = localStorage.getItem("invoiceLineRules");
    return saved ? JSON.parse(saved) : [];
  });
  const [ruleFormData, setRuleFormData] = useState({
    name: "",
    priority: 1,
    active: true,
    conditionLogic: "AND",
    conditions: [],
    actionType: "",
    actionConfig: {}
  });
  const costCenters = ["CAS", "DDR", "IT", "WDC", "LS", "RMC"];
  useEffect(() => {
    const savedClients = localStorage.getItem("clients");
    if (savedClients) {
      setClients(JSON.parse(savedClients));
    }
    const savedJackets = localStorage.getItem("jackets");
    if (savedJackets) {
      setJackets(JSON.parse(savedJackets));
    }
  }, []);
  useEffect(() => {
    if (!selectedClient || !selectedJacket) {
      setInvoiceElements([]);
      return;
    }
    const savedProfiles = localStorage.getItem("billingProfiles");
    if (!savedProfiles) {
      setInvoiceElements([]);
      return;
    }
    const profiles = JSON.parse(savedProfiles);
    let matchingProfile = profiles.find(
      (p) => p.clientCode === selectedClient && p.jacketNumber === selectedJacket
    );
    if (!matchingProfile) {
      setInvoiceElements([]);
      return;
    }
    let jacketServices = matchingProfile.services || [];
    const elements = jacketServices.map((service, index) => ({
      id: index + 1,
      invoiceDescription: service.invoiceDescription || service.serviceName || "",
      billingType: service.serviceType || "Activity Based",
      calculationType: service.unitType || "Quantity \xD7 Rate",
      unitType: service.unitType || "",
      rate: service.rate || service.clientRate || service.contractRate || "",
      frequency: service.frequency || "",
      effectiveDate: service.effectiveDate || "",
      endDate: service.endDate || "",
      active: true
    }));
    setInvoiceElements(elements);
  }, [selectedClient, selectedJacket]);
  const filteredJackets = selectedClient ? jackets.filter((j) => {
    const client = clients.find((c) => c.code === selectedClient);
    return client && j.clientId === client.id;
  }) : [];
  const toggleElementExpansion = (elementId) => {
    if (expandedElement === elementId) {
      setExpandedElement(null);
      setShowRuleBuilder(false);
      setEditingRule(null);
    } else {
      setExpandedElement(elementId);
      setShowRuleBuilder(false);
    }
  };
  const handleAddRule = (elementId) => {
    setRuleFormData({
      name: "",
      priority: invoiceLineRules.filter((r) => r.invoiceLineId === elementId).length + 1,
      active: true,
      conditionLogic: "AND",
      conditions: [],
      actionType: "",
      actionConfig: {}
    });
    setEditingRule(null);
    setExpandedElement(elementId);
    setShowRuleBuilder(true);
  };
  const handleEditRule = (rule) => {
    setRuleFormData({
      name: rule.name,
      priority: rule.priority,
      active: rule.active,
      conditionLogic: rule.conditionLogic,
      conditions: rule.conditions,
      actionType: rule.actionType,
      actionConfig: rule.actionConfig
    });
    setEditingRule(rule);
    setExpandedElement(rule.invoiceLineId);
    setShowRuleBuilder(true);
  };
  const handleSaveRule = () => {
    if (!expandedElement) return;
    let updatedRules = [...invoiceLineRules];
    if (editingRule) {
      updatedRules = updatedRules.map(
        (r) => r.id === editingRule.id ? { ...editingRule, ...ruleFormData } : r
      );
    } else {
      const newId = updatedRules.length > 0 ? Math.max(...updatedRules.map((r) => r.id)) + 1 : 1;
      const newRule = {
        id: newId,
        invoiceLineId: expandedElement,
        ...ruleFormData,
        actionType: ruleFormData.actionType
      };
      updatedRules.push(newRule);
    }
    localStorage.setItem("invoiceLineRules", JSON.stringify(updatedRules));
    setInvoiceLineRules(updatedRules);
    setShowRuleBuilder(false);
    setEditingRule(null);
  };
  const handleCancelRule = () => {
    setShowRuleBuilder(false);
    setEditingRule(null);
  };
  const handleDeleteRule = (ruleId) => {
    if (window.confirm("Are you sure you want to delete this rule?")) {
      const updatedRules = invoiceLineRules.filter((r) => r.id !== ruleId);
      localStorage.setItem("invoiceLineRules", JSON.stringify(updatedRules));
      setInvoiceLineRules(updatedRules);
    }
  };
  const handleToggleRuleActive = (rule) => {
    const updatedRules = invoiceLineRules.map(
      (r) => r.id === rule.id ? { ...r, active: !r.active } : r
    );
    localStorage.setItem("invoiceLineRules", JSON.stringify(updatedRules));
    setInvoiceLineRules(updatedRules);
  };
  const moveRule = (elementId, dragIndex, hoverIndex) => {
    const elementRules = invoiceLineRules.filter((r) => r.invoiceLineId === elementId).sort((a, b) => a.priority - b.priority);
    const dragRule = elementRules[dragIndex];
    const newElementRules = [...elementRules];
    newElementRules.splice(dragIndex, 1);
    newElementRules.splice(hoverIndex, 0, dragRule);
    const updatedRules = invoiceLineRules.map((r) => {
      if (r.invoiceLineId !== elementId) return r;
      const index = newElementRules.findIndex((er) => er.id === r.id);
      return { ...r, priority: index + 1 };
    });
    localStorage.setItem("invoiceLineRules", JSON.stringify(updatedRules));
    setInvoiceLineRules(updatedRules);
  };
  const handleAddCondition = () => {
    const newCondition = {
      id: Date.now().toString(),
      field: "",
      operator: "equals",
      value: ""
    };
    setRuleFormData((prev) => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  };
  const handleUpdateCondition = (id, field, value) => {
    setRuleFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.map(
        (c) => c.id === id ? { ...c, [field]: value } : c
      )
    }));
  };
  const handleRemoveCondition = (id) => {
    setRuleFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((c) => c.id !== id)
    }));
  };
  const renderActionConfig = () => {
    switch (ruleFormData.actionType) {
      case "Discount":
        return <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Discount Type
              </label>
              <select
          value={ruleFormData.actionConfig.discountType || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, discountType: e.target.value }
          }))}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
                <option value="">Select type</option>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Discount Value
              </label>
              <input
          type="number"
          value={ruleFormData.actionConfig.discountValue || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, discountValue: e.target.value }
          }))}
          placeholder="Enter value"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
            </div>
          </div>;
      case "Reallocate Percentage":
        return <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Percentage
              </label>
              <input
          type="number"
          value={ruleFormData.actionConfig.percentage || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, percentage: e.target.value }
          }))}
          placeholder="Enter percentage"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Cost Center
              </label>
              <select
          value={ruleFormData.actionConfig.targetCostCenter || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, targetCostCenter: e.target.value }
          }))}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
                <option value="">Select cost center</option>
                {costCenters.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
              </select>
            </div>
          </div>;
      case "Split Charge":
        return <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Split Ratio (%)
              </label>
              <input
          type="number"
          value={ruleFormData.actionConfig.splitRatio || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, splitRatio: e.target.value }
          }))}
          placeholder="50"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Cost Center
              </label>
              <select
          value={ruleFormData.actionConfig.targetCostCenter || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, targetCostCenter: e.target.value }
          }))}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
                <option value="">Select cost center</option>
                {costCenters.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
              </select>
            </div>
          </div>;
      case "Override Rate":
        return <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Rate
              </label>
              <input
          type="number"
          value={ruleFormData.actionConfig.newRate || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, newRate: e.target.value }
          }))}
          placeholder="Enter rate"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
            </div>
          </div>;
      case "Add Flat Fee":
        return <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Flat Fee Amount
              </label>
              <input
          type="number"
          value={ruleFormData.actionConfig.flatFeeAmount || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, flatFeeAmount: e.target.value }
          }))}
          placeholder="Enter amount"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fee Description
              </label>
              <input
          type="text"
          value={ruleFormData.actionConfig.feeDescription || ""}
          onChange={(e) => setRuleFormData((prev) => ({
            ...prev,
            actionConfig: { ...prev.actionConfig, feeDescription: e.target.value }
          }))}
          placeholder="Enter description"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <DndProvider backend={HTML5Backend}>
      <div>
        {
    /* Header */
  }
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Invoice Configuration</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Configure invoice rules for contract-driven invoice elements
          </p>
        </div>

        {
    /* Sticky Filter Bar */
  }
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-4 mb-6 shadow-sm">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client ID *
              </label>
              <select
    value={selectedClient}
    onChange={(e) => {
      setSelectedClient(e.target.value);
      setSelectedJacket("");
      setExpandedElement(null);
      setShowRuleBuilder(false);
    }}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                <option value="">Select client...</option>
                {clients.map((client) => <option key={client.id} value={client.code}>
                    {client.name} ({client.code})
                  </option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Jacket *
              </label>
              <select
    value={selectedJacket}
    onChange={(e) => {
      setSelectedJacket(e.target.value);
      setExpandedElement(null);
      setShowRuleBuilder(false);
    }}
    disabled={!selectedClient}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
  >
                <option value="">Select jacket...</option>
                {filteredJackets.map((jacket) => <option key={jacket.id} value={jacket.jobNumber}>
                    {jacket.jobNumber} - {jacket.description1 || jacket.programId || "No description"}
                  </option>)}
              </select>
            </div>
          </div>
        </div>

        {
    /* Table View */
  }
        {invoiceElements.length > 0 ? <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Invoice Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Billing Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Calculation Type
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Rules
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {invoiceElements.map((element) => {
    const elementRules = invoiceLineRules.filter((r) => r.invoiceLineId === element.id).sort((a, b) => a.priority - b.priority);
    const isExpanded = expandedElement === element.id;
    return <React.Fragment key={element.id}>
                      {
      /* Table Row */
    }
                      <tr
      onClick={() => toggleElementExpansion(element.id)}
      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isExpanded ? "bg-blue-50 dark:bg-blue-900/10" : ""}`}
    >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {element.invoiceDescription}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <select
      value={element.billingType}
      onChange={(e) => {
        const updated = invoiceElements.map(
          (el) => el.id === element.id ? { ...el, billingType: e.target.value } : el
        );
        setInvoiceElements(updated);
        const savedProfiles = localStorage.getItem("billingProfiles");
        if (savedProfiles) {
          const profiles = JSON.parse(savedProfiles);
          const updatedProfiles = profiles.map((profile) => {
            if (profile.clientCode === selectedClient) {
              return {
                ...profile,
                services: profile.services.map((service) => {
                  if (service.invoiceDescription === element.invoiceDescription && service.jacketNumber === selectedJacket) {
                    return { ...service, serviceType: e.target.value };
                  }
                  return service;
                })
              };
            }
            return profile;
          });
          localStorage.setItem("billingProfiles", JSON.stringify(updatedProfiles));
        }
      }}
      className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    >
                            <option value="Activity Based">Activity Based</option>
                            <option value="Flat Fee">Flat Fee</option>
                            <option value="Calculated">Calculated</option>
                            <option value="Manual">Manual</option>
                            <option value="Pass Through">Pass Through</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <select
      value={element.calculationType}
      onChange={(e) => {
        const updated = invoiceElements.map(
          (el) => el.id === element.id ? { ...el, calculationType: e.target.value } : el
        );
        setInvoiceElements(updated);
        const savedProfiles = localStorage.getItem("billingProfiles");
        if (savedProfiles) {
          const profiles = JSON.parse(savedProfiles);
          const updatedProfiles = profiles.map((profile) => {
            if (profile.clientCode === selectedClient) {
              return {
                ...profile,
                services: profile.services.map((service) => {
                  if (service.invoiceDescription === element.invoiceDescription && service.jacketNumber === selectedJacket) {
                    return { ...service, unitType: e.target.value };
                  }
                  return service;
                })
              };
            }
            return profile;
          });
          localStorage.setItem("billingProfiles", JSON.stringify(updatedProfiles));
        }
      }}
      className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    >
                            <option value="Quantity × Rate">Quantity × Rate</option>
                            <option value="Fixed Amount">Fixed Amount</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Tiered">Tiered</option>
                            <option value="Derived Formula">Derived Formula</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {elementRules.length > 0 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                              {elementRules.length}
                            </span> : <span className="text-sm text-gray-400 dark:text-gray-600">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
      onClick={(e) => {
        e.stopPropagation();
        handleAddRule(element.id);
      }}
      className="app-btn-plain inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
    >
                            <Plus className="w-4 h-4" />
                            Add Rule
                          </button>
                        </td>
                      </tr>

                      {
      /* Expanded Panel */
    }
                      {isExpanded && <tr>
                          <td colSpan={5} className="px-0 py-0">
                            <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                              {
      /* Section A — Existing Rules */
    }
                              {!showRuleBuilder && elementRules.length > 0 && <div className="mb-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                      Existing Rules
                                    </h4>
                                    <button
      onClick={() => setShowRuleBuilder(true)}
      className="app-btn-plain inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
    >
                                      <Plus className="w-4 h-4" />
                                      Add Another Rule
                                    </button>
                                  </div>
                                  {elementRules.map((rule, index) => <RuleCard
      key={rule.id}
      rule={rule}
      index={index}
      moveRule={(dragIndex, hoverIndex) => moveRule(element.id, dragIndex, hoverIndex)}
      onEdit={handleEditRule}
      onDelete={handleDeleteRule}
      onToggleActive={handleToggleRuleActive}
    />)}
                                </div>}

                              {
      /* Section B — Add / Edit Rule Builder */
    }
                              {showRuleBuilder && <div className="border-2 border-blue-600 dark:border-blue-400 rounded bg-white dark:bg-gray-900 p-5 space-y-4">
                                  <h5 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                    {editingRule ? "Edit Rule" : "Add New Rule"}
                                  </h5>

                                  {
      /* Rule Name */
    }
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Rule Name *
                                    </label>
                                    <input
      type="text"
      value={ruleFormData.name}
      onChange={(e) => setRuleFormData((prev) => ({ ...prev, name: e.target.value }))}
      placeholder="e.g., 10% Volume Discount"
      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    />
                                  </div>

                                  {
      /* Rule Type */
    }
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Rule Type *
                                    </label>
                                    <select
      value={ruleFormData.actionType}
      onChange={(e) => setRuleFormData((prev) => ({
        ...prev,
        actionType: e.target.value,
        actionConfig: {}
      }))}
      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    >
                                      <option value="">Select rule type</option>
                                      <option value="Discount">Discount</option>
                                      <option value="Reallocate Percentage">Reallocate Percentage</option>
                                      <option value="Split Charge">Split Charge</option>
                                      <option value="Override Rate">Override Rate</option>
                                      <option value="Add Flat Fee">Add Flat Fee</option>
                                    </select>
                                  </div>

                                  {
      /* Conditions Section */
    }
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Conditions (Optional)
                                      </label>
                                      <div className="flex gap-2">
                                        <button
      onClick={() => setRuleFormData((prev) => ({ ...prev, conditionLogic: "AND" }))}
      className={`px-2 py-1 text-xs ${ruleFormData.conditionLogic === "AND" ? "app-btn-gloss" : "app-btn-plain bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"}`}
    >
                                          AND
                                        </button>
                                        <button
      onClick={() => setRuleFormData((prev) => ({ ...prev, conditionLogic: "OR" }))}
      className={`px-2 py-1 text-xs ${ruleFormData.conditionLogic === "OR" ? "app-btn-gloss" : "app-btn-plain bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"}`}
    >
                                          OR
                                        </button>
                                      </div>
                                    </div>

                                    {ruleFormData.conditions.map((condition, idx) => <div key={condition.id} className="border border-gray-200 dark:border-gray-700 rounded p-3 mb-2 bg-gray-50 dark:bg-gray-800">
                                        <div className="grid grid-cols-3 gap-2">
                                          <div>
                                            <select
      value={condition.field}
      onChange={(e) => handleUpdateCondition(condition.id, "field", e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    >
                                              <option value="">Select field</option>
                                              <option value="quantity">Quantity</option>
                                              <option value="amount">Amount</option>
                                              <option value="date">Date</option>
                                              <option value="employee">Employee</option>
                                            </select>
                                          </div>
                                          <div>
                                            <select
      value={condition.operator}
      onChange={(e) => handleUpdateCondition(condition.id, "operator", e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    >
                                              <option value="equals">Equals</option>
                                              <option value="not_equals">Not Equals</option>
                                              <option value="greater_than">Greater Than</option>
                                              <option value="less_than">Less Than</option>
                                              <option value="contains">Contains</option>
                                            </select>
                                          </div>
                                          <div className="flex gap-2">
                                            <input
      type="text"
      value={condition.value}
      onChange={(e) => handleUpdateCondition(condition.id, "value", e.target.value)}
      placeholder="Value"
      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
    />
                                            <button
      onClick={() => handleRemoveCondition(condition.id)}
      className="app-btn-gloss p-2"
    >
                                              <X className="w-3 h-3 text-red-600 dark:text-red-400" />
                                            </button>
                                          </div>
                                        </div>
                                        {idx < ruleFormData.conditions.length - 1 && <div className="mt-2 text-center">
                                            <span className="px-2 py-1 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                                              {ruleFormData.conditionLogic}
                                            </span>
                                          </div>}
                                      </div>)}

                                    <button
      onClick={handleAddCondition}
      className="app-btn-gloss w-full text-xs"
    >
                                      <Plus className="w-3 h-3" />
                                      Add Condition
                                    </button>
                                  </div>

                                  {
      /* Action Configuration */
    }
                                  {ruleFormData.actionType && <div className="border border-gray-200 dark:border-gray-700 rounded p-4 bg-gray-50 dark:bg-gray-800">
                                      <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Action Configuration</h6>
                                      {renderActionConfig()}
                                    </div>}

                                  {
      /* Preview Section */
    }
                                  {ruleFormData.actionType && <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                      <h6 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Preview</h6>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Before</div>
                                          <div className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-900 text-xs space-y-1">
                                            <div className="flex justify-between">
                                              <span className="text-gray-600 dark:text-gray-400">Amount:</span>
                                              <span className="text-gray-900 dark:text-gray-100">$1,000.00</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-gray-600 dark:text-gray-400">Cost Center:</span>
                                              <span className="text-gray-900 dark:text-gray-100">IT</span>
                                            </div>
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">After</div>
                                          <div className="border border-blue-200 dark:border-blue-700 rounded p-3 bg-blue-50 dark:bg-blue-900/20 text-xs space-y-1">
                                            <div className="flex justify-between">
                                              <span className="text-gray-600 dark:text-gray-400">Amount:</span>
                                              <span className="text-blue-600 dark:text-blue-400 font-medium">
                                                {ruleFormData.actionType === "Discount" && ruleFormData.actionConfig.discountValue ? `$${(1e3 - (ruleFormData.actionConfig.discountType === "percentage" ? 1e3 * parseFloat(ruleFormData.actionConfig.discountValue) / 100 : parseFloat(ruleFormData.actionConfig.discountValue))).toFixed(2)}` : "$1,000.00"}
                                              </span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-gray-600 dark:text-gray-400">Cost Center:</span>
                                              <span className="text-gray-900 dark:text-gray-100">
                                                {ruleFormData.actionConfig.targetCostCenter || "IT"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>}

                                  {
      /* Action Buttons */
    }
                                  <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <button
      onClick={handleSaveRule}
      disabled={!ruleFormData.name || !ruleFormData.actionType}
      className="app-btn-gloss text-sm disabled:opacity-50 disabled:cursor-not-allowed"
    >
                                      <Save className="w-4 h-4" />
                                      {editingRule ? "Update Rule" : "Save Rule"}
                                    </button>
                                    <button
      onClick={handleCancelRule}
      className="app-btn-gloss-cancel text-sm"
    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>}

                              {
      /* Show "Add Rule" button when no rules exist */
    }
                              {!showRuleBuilder && elementRules.length === 0 && <div className="text-center py-4">
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">No rules configured yet</p>
                                  <button
      onClick={() => setShowRuleBuilder(true)}
      className="app-btn-plain inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded px-3 py-1.5 transition-colors"
    >
                                    <Plus className="w-4 h-4" />
                                    Add First Rule
                                  </button>
                                </div>}
                            </div>
                          </td>
                        </tr>}
                    </React.Fragment>;
  })}
              </tbody>
            </table>
          </div> : <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900">
            <p className="text-gray-600 dark:text-gray-400">
              {!selectedClient || !selectedJacket ? "Select a client and jacket to view invoice elements" : "No invoice elements found for the selected client and jacket"}
            </p>
            {selectedClient && selectedJacket && <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                Configure contract data in Billing Profile Configuration first
              </p>}
          </div>}
      </div>
    </DndProvider>;
}
export {
  InvoiceConfiguration
};
